import React, { useContext, useEffect, useMemo, useState } from 'react'
import { StatsCard } from '../../../components/charts/StatsCard'
import { Calendar, FolderClosed } from 'lucide-react'
import MultiLineChart from '../../../components/charts/MultilineChart'
import { User } from '../../../lib/types/User'
import { useGetCurrencyMetrics } from '../../../api/reports/useGetCurrencyMetrics'
import { useGetCurrencyGains } from '../../../api/reports/useGetCurrencyGains'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../components/skeleton/Accordion'
import SelectInput from '../../../components/FormElements/SelectInput'
import { formatCurrency } from '../../../lib/utils/formatNumber'
import { getCurrencyIcon } from './Dividends'
import { DateRangeContext } from '../../../context/DateRangeProvider'
import { Skeleton } from '../../../components/skeleton/Skeleton'

const Currency = ({
  user,
  currencies,
}: {
  user?: User
  currencies?: Array<{
    id: number
    name: string
    code: string
    symbol: string
  }>
}): JSX.Element => {
  const { dateRange } = useContext(DateRangeContext)

  const { data: currencyData, isLoading } = useGetCurrencyMetrics({ dateRange })
  const currencyOptions = useMemo(
    () =>
      currencies?.map((d) => {
        return {
          label: `${d.name} (${d.symbol})`,
          value: d.id,
        }
      }),
    [currencies],
  )

  const [currency, setCurrency] = useState<
    | {
        label: string
        value: number
      }
    | undefined
  >(currencyOptions?.[0])
  const [includeEquity, setIncludeEquity] = useState<
    | {
        label: string
        value: boolean
      }
    | undefined
  >({
    label: 'Include',
    value: true,
  })

  useEffect(() => {
    setCurrency(currencyOptions?.[0])
  }, [currencyOptions])

  const { data: currencyGains } = useGetCurrencyGains({
    filters: {
      base_currency_id: Number(currency?.value) || 1,
      include_equity: includeEquity?.value ? 1 : 0,
      startDate: dateRange.dateRange.startDate,
      endDate: dateRange.dateRange.endDate,
    },
  })

  const groupedData = useMemo(() => {
    if (!currencyData?.length) return []

    return currencyData.map(
      ({ date, local_currency_symbol, target_currency_symbol, amount_sent }) => ({
        date,
        // exchange: `${local_currency_symbol} to ${target_currency_symbol}`,
        [`${local_currency_symbol} to ${target_currency_symbol}`]: amount_sent,
      }),
    )
  }, [currencyData])

  const datasets = useMemo(
    () =>
      Object.keys(
        groupedData.reduce((acc, item) => {
          Object.keys(item).forEach((key) => {
            if (key !== 'date') acc[key] = true
          })
          return acc
        }, {} as Record<string, boolean>),
      ).map((code, i) => ({
        yKey: code,
        label: currencies?.find((c) => c.code === code)?.name,
        colorScheme: i === 0 ? ('crimson-sunset' as const) : undefined,
      })),
    [groupedData, currencies],
  )

  return (
    <div className="w-full flex flex-col gap-5 py-6">
      <div className="grid grid-cols-2 sm:grid-cols-2 gap-4">
        <StatsCard
          isLoading={isLoading}
          title={'Total Uploads'}
          value={new Set(currencyData?.map((d) => d.upload_id)).size || 0}
          icon={<FolderClosed />}
        />
        <StatsCard
          isLoading={isLoading}
          title={'Total Exchanges'}
          value={currencyData?.length || 0}
          icon={<Calendar />}
        />
      </div>

      <Accordion type="single" collapsible>
        <AccordionItem value="item-1">
          <AccordionTrigger>Calculation Options</AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-2 gap-2">
              <SelectInput
                label="Currency"
                name={'currency'}
                options={currencyOptions ?? []}
                infoMessage={`Gains vs ${user?.target_currency_code.toUpperCase()}`}
                vanilla
                value={currency}
                onChange={(e) => {
                  e && setCurrency({ label: e?.label, value: e?.value })
                }}
              />

              <SelectInput
                label="Dividends & Stocks"
                name={'include_equity'}
                options={[
                  {
                    label: 'Include',
                    value: true,
                  },
                  { label: 'Exclude', value: false },
                ]}
                vanilla
                value={includeEquity}
                onChange={(e) => {
                  e && setIncludeEquity({ label: e?.label, value: e?.value })
                }}
              />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      <StatsCard
        isLoading={isLoading}
        title={`Currency Gains`}
        value={formatCurrency(currencyGains?.data, user?.target_currency_code)}
        icon={getCurrencyIcon(user?.target_currency_code)}
        className="col-span-2 sm:col-span-1 border-2 border-lime-600 bg-lime-500 dark:bg-green-800"
      />

      <div className="grid grid-cols-1 sm:grid-cols-1 gap-4">
        {isLoading ? (
          <Skeleton className="h-[300px] w-full rounded-lg" />
        ) : (
          <div
            className={'min-h-[270px] border-2 border-foreground shadow-lg rounded-xl'}>
            <div
              className={
                'pt-5 pb-2 px-5 flex justify-between font-bold text-foreground text-lg'
              }>
              <span>Amount Sent</span>
            </div>
            <div className={'h-[270px] p-2'}>
              <MultiLineChart
                data={groupedData ?? []}
                datasets={datasets ?? []}
                customOptions={{
                  elements: {
                    line: {
                      tension: 0,
                    },
                    point: {
                      radius: 0,
                    },
                  },
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Currency
